import { useEffect, useRef, useState } from 'react'

/**
 * Hook that ensures a loading state is displayed for a minimum duration.
 * Useful for preventing flickering when loading completes very quickly.
 *
 * @param isLoading - The current loading state
 * @param minDisplayTimeMs - Minimum time in milliseconds to display the loader (default: 1000ms)
 * @returns Whether the loader should be shown (true if loading or within minimum display time)
 */
export function useMinimumDisplayTime(isLoading: boolean, minDisplayTimeMs = 1000): boolean {
  // Initialize showLoader based on initial isLoading state
  const [showLoader, setShowLoader] = useState(isLoading)
  const loadingStartTimeRef = useRef<number | null>(isLoading ? Date.now() : null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isLoading) {
      // Start loading - record the start time
      if (loadingStartTimeRef.current === null) {
        loadingStartTimeRef.current = Date.now()
      }
      setShowLoader(true)
    } else if (loadingStartTimeRef.current !== null) {
      // Loading finished - ensure minimum display time
      const elapsed = Date.now() - loadingStartTimeRef.current
      const minDisplayTime = minDisplayTimeMs

      if (elapsed < minDisplayTime) {
        // Wait until minimum display time is reached
        const remainingTime = minDisplayTime - elapsed
        timeoutRef.current = setTimeout(() => {
          setShowLoader(false)
          loadingStartTimeRef.current = null
        }, remainingTime)
      } else {
        // Already displayed for at least minimum time, hide immediately
        setShowLoader(false)
        loadingStartTimeRef.current = null
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isLoading, minDisplayTimeMs])

  return showLoader
}
